// biome-ignore-all lint/suspicious/noArrayIndexKey: flagged lists are summary tables derived from form state on each render — order is deterministic, items are never mutated mid-render.
// biome-ignore-all lint/a11y/useSemanticElements: role="button" is intentional; the element wraps nested interactive children, so it cannot be a native button (Task #25).
// biome-ignore-all lint/a11y/noStaticElementInteractions: Files collapsible header uses div[role=button] to avoid button-inside-button nesting (HelpButton renders a <button> inside it).
// biome-ignore-all lint/a11y/useKeyWithClickEvents: same as above — onKeyDown IS present on the div[role=button] toggle.
//
// =============================================================================
// RequestWizard.jsx
// =============================================================================
// The shelter-facing multi-step form for creating or editing a request.
// Owns local form state, validation, draft persistence, and the final atomic
// save (upsert_request_atomic RPC). The 11 inner Tab*/helper components are
// stitched together by RequestWizard / RequestForm at the bottom.
//
// TABLE OF CONTENTS
// ─────────────────
//   Child-level navigation + overview
//     ChildTabBar           (line   ~78) — horizontal tab bar across children
//     ChildTab              (line  ~218) — one child's stacked form sections
//     RequestOverview       (line  ~518) — final summary panel before submit
//   Step tabs (one per request section)
//     TabGeneral            → extracted to ./TabGeneral.jsx
//     TabFiles              (line ~3759) — invoices + supporting documents upload
//   Orchestrators (the actual top-level exports)
//     RequestWizard         (line ~4338) — the wizard for NEW requests (paginated steps)
//     RequestForm           (line ~5812) — the form for EDITING existing requests (all on one page)
//
// DATA FLOW
// ─────────
//   - Local React state holds the in-progress request (children array, header
//     fields, files, etc.). Mutations go through tiny update helpers per tab.
//   - Drafts are auto-saved into `requests` with a "Draft" status and a
//     DRAFT-* ref_no until the user submits.
//   - On submit, the entire request + its children are saved atomically via
//     the `upsert_request_atomic` RPC (which delegates to save_request_children).
//   - Delivery-address text is classified into id-or-override via the
//     resolveDeliveryForSave helper at every write site.
//
// SEE ALSO
// ────────
//   src/components/form/ChildSections.jsx — per-child form sections used by ChildTab
//   src/components/form/FormUI.jsx        — shared form atoms (CostFld, Sel, etc.)
//   src/utils/formConstants.js            — fee/uniform/device catalogues + validators
//   docs/db-1-phase-3b-delivery-address-switch.sql — current upsert_request_atomic body
// =============================================================================

import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { useShowDeviceDelivery, useStockTopUpEnabled } from "../../app/UiConfigContext.jsx";
import {
  getMaxRefNoForYear,
  nextRefNo as nextRefNoRpc,
  upsertRequestAtomic,
} from "../../data/requests";
import { listExistingSchoolNames } from "../../data/schools";
import { getShelterById, getShelterColleagues } from "../../data/shelters";
import { submitStockTopUp } from "../../data/stockTopUp";
import { getPrefillStatus } from "../../data/stockTopUpPrefill";
import { useSectionBlocks } from "../../hooks/useSectionBlocks";
import { useStandardCosts } from "../../hooks/useStandardCosts";
import { resolveDeliveryForSubmit } from "../../services/deliveryCapture";
import { alertDialog, confirmDialog } from "../../services/dialogService";
import { otherSchoolCandidates, otherSchoolsToAlert } from "../../services/otherSchoolAlert";
import { nextStatusOnSubmit } from "../../services/requestLifecycle";
import { requestStamp } from "../../services/requestStamp";
import { sectionBlockLabel, stripBlockedSections } from "../../services/sectionBlocks";
import { emptyTopUpState } from "../../services/stockTopUpForm";
import { prefillSubmitBlock } from "../../services/topUpPrefill";
import { createTask, sendEmail } from "../../utils/actions";
import { logAudit } from "../../utils/auditLog";
import {
  calcUniItems,
  DEFAULT_STANDARD_COSTS,
  deviceCost,
  isPrimary,
  isValidAUPhone,
  UNIFORM_ITEMS,
  WINTER_ITEMS,
} from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { money } from "../../utils/helpers";
import { formatPhone, sanitizeNote, sanitizePhone, sanitizeText } from "../../utils/sanitize";
import { contactNameForUser } from "../../utils/sessionUser";
import { Btn, Card } from "../../utils/ui.jsx";
import { HelpButton } from "./ChildSections.jsx";
import { ChildTab } from "./ChildTab.jsx";
import { ChildTabBar } from "./ChildTabBar.jsx";
import { SubmittedScreen, WizardStepBar } from "./FormUI.jsx";
import { makeChild } from "./makeChild.js";
import { RequestForm } from "./RequestForm.jsx";
import { RequestOverview } from "./RequestOverview.jsx";
import StockTopUpStep from "./StockTopUpStep.jsx";
import { TabFiles } from "./TabFiles.jsx";
import { TabGeneral } from "./TabGeneral.jsx";

// makeChild imported from ./makeChild.js — shared with RequestForm.jsx.

// ChildTabBar moved to ./ChildTabBar.jsx — imported above.

// ChildTab + CHILD_NEED_KEYS/LABELS moved to ./ChildTab.jsx — imported above.

// RequestOverview moved to ./RequestOverview.jsx — imported above.

// ── END CHILD-CENTRIC COMPONENTS ─────────────────────────────────────────────

// TabStationery removed — stationery and textbooks now handled as line items in TabFees (School Costs & Stationery tab)

// TabFiles moved to ./TabFiles.jsx — imported above.

// ── REQUEST WIZARD (3-step shelter create flow) ───────────────────────────────
// Replaces RequestForm for shelter users creating new requests.
// Step 1: General Information
// Step 2: Children & Needs
// Step 3: Review & Submit
// Same props/contract as RequestForm so App can swap them transparently.

// ─────────────────────────────────────────────────────────────────────────────
// RequestWizard — paginated wizard for NEW requests
// ─────────────────────────────────────────────────────────────────────────────
// Renders: the multi-step wizard a shelter user walks through to create a
//          new request — General → Fees → Uniforms → Shoes → Laptop → Files
//          → Overview/Submit. Auto-saves drafts to `requests` as the user
//          progresses. Submit calls upsert_request_atomic.
// Props (selected):
//   user, draftId, draftRefNo  - identity + draft persistence
//   onComplete                 - parent callback when submission succeeds
//   onCancel                   - parent callback when user bails out
//   getPendingDraftId          - helper to resolve the active draft id
// Notes: validation runs per-step; users can't advance until the current
//        step's required fields are populated. resolveDeliveryForSave is
//        used at submit time to classify the delivery string.
function RequestWizard({
  onBack,
  onSaveDraft,
  initialData,
  draftId,
  draftRefNo,
  saveDraftRef,
  getPendingDraftId,
}) {
  const { user } = useSession();
  const _isQLD = user.state === "QLD";
  const _sc = useStandardCosts();
  const standardCosts = _sc.costs;
  // Admin-controlled per-device delivery visibility (default off). When hidden, the
  // per-device delivery address is not captured, so it isn't validated either.
  const showDeviceDelivery = useShowDeviceDelivery();
  // Stock Top-ups availability (System Setup → Config). When the global flag is off,
  // the Stock Top-up option is disabled and reads "Not available at the moment".
  // TEST services are treated like any other service (no special-case — 13 Sep 2026).
  const stockTopUpEnabled = useStockTopUpEnabled();
  const topUpAvailable = stockTopUpEnabled;

  // ── State ──────────────────────────────────────────────────────────────────
  const _step = React.useState(function () {
    // If resuming a draft with children already filled, start on step 2
    if (initialData?.children && initialData.children.length > 0) {
      const firstChild = initialData.children[0];
      const hasChildData = !!(
        firstChild.gender ||
        firstChild.yearLevel ||
        firstChild.schoolName ||
        (firstChild.fees && firstChild.fees.length > 0) ||
        firstChild.uniforms ||
        firstChild.shoes ||
        firstChild.laptop
      );
      if (hasChildData) return 2;
    }
    return 1;
  });
  const step = _step[0];
  const setStep = _step[1];

  // Request-type modal (stock-top-ups plan): shown ONCE when the shelter clicks NEXT on step 1 of a
  // BRAND-NEW request. Two choices — "Fit out Children" (the standard flow) or "Stock Top-up" (db-154
  // Phase 1: forks to the top-up step 2). Skipped on a resumed draft / edit (typeLockedRef starts
  // true). Deliberately isolated: goToStep2 gates on it and the fit-out path is untouched.
  const _typeModal = React.useState(false);
  const typeModal = _typeModal[0];
  const setTypeModal = _typeModal[1];
  // typeLockedRef: once the shelter makes a selection on step 2 (any child Gender/Year/School/
  // need), the type is LOCKED and Back → NEXT no longer re-asks. Before that they can revisit the
  // choice. Starts locked for a resumed draft / edit (initialData present).
  const typeLockedRef = React.useRef(!!initialData);
  // typeAckRef: a ONE-TIME "I acknowledged the modal, let me through" flag so the modal's button
  // advances WITHOUT locking in. Consumed on the goToStep2 pass it enables.
  const typeAckRef = React.useRef(false);
  // db-154 (stock-top-ups Phase 1): the request kind being built. 'fit_out' is the standard wizard
  // flow (untouched); 'stock_top_up' forks step 2 to the top-up capture. Chosen in the type modal;
  // seeded from a resumed draft/edit, else 'fit_out'. ISOLATED — only the top-up branch reads it.
  const _requestType = React.useState(initialData?.requestType || "fit_out");
  const requestType = _requestType[0];
  const setRequestType = _requestType[1];
  // Stock Top-up form state, lifted here so it survives step 1 <-> 2 navigation (like
  // the fit-out general/children state). Shape + helpers live in stockTopUpForm.ts.
  // Phase 5: seed from a staff-prepared prefill when the service opened one via the
  // deep-link (initialData.topUpState is the stored TopUpState payload); else a fresh
  // form. Merged over emptyTopUpState() so a partial/legacy payload can't drop keys.
  const _topUp = React.useState(() =>
    initialData?.topUpState
      ? { ...emptyTopUpState(), ...initialData.topUpState }
      : emptyTopUpState()
  );
  const topUpState = _topUp[0];
  const setTopUpState = _topUp[1];

  let defaultDelivery = "";
  if (initialData?.deliveryAddress) {
    defaultDelivery = initialData.deliveryAddress;
  } else if (user.deliveryAddresses && user.deliveryAddresses.length > 0) {
    const defAddr = user.deliveryAddresses[0];
    defaultDelivery = [
      defAddr.street,
      defAddr.street2,
      defAddr.suburb_city || defAddr.suburb,
      defAddr.state,
      defAddr.postcode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  // Phase 5: a staff-prepared top-up (deep-link) — initialData carries only the top-up
  // content + a client-ref default, so the step-1 envelope must seed FRESH from the
  // caseworker's session (like a brand-new request), NOT from the empty initialData draft
  // fields. Without this, Contact Mobile/Phone came through blank (smoke, 13 Aug).
  const isPrefill = !!initialData?.prefillId;
  const _gen = React.useState(
    isPrefill
      ? {
          clientRef: initialData.shelterRef || "",
          phone: user.phones && user.phones.length > 0 ? user.phones[0].number || "" : "",
          mobile: user.contactMobile || "",
          delivery: defaultDelivery,
          deliveryParts: null,
          notes: "",
        }
      : initialData
        ? {
            clientRef: initialData.shelterRef || "",
            phone: initialData.phone || "",
            mobile: initialData.mobile || "",
            delivery: initialData.deliveryAddress || defaultDelivery,
            // Structured parts of a picked one-off address (verified-address model).
            // Not restored on a draft reopen — dedupe at submit re-links the existing
            // row from the address string, so parts aren't needed to preserve it.
            deliveryParts: null,
            notes: initialData.caseworkerNote || "",
          }
        : {
            clientRef: "",
            phone: user.phones && user.phones.length > 0 ? user.phones[0].number || "" : "",
            mobile: user.contactMobile || "",
            delivery: defaultDelivery,
            deliveryParts: null,
            notes: "",
          }
  );
  const general = _gen[0];
  const setGeneral = _gen[1];

  // Phase 5: a prefill deep-link lands with step 1 pre-filled from the caseworker's
  // session — skip straight to the (read-only) top-up review when step 1 already validates;
  // otherwise stay so the shelter can complete it. Runs once on entry. validateStep1 is a
  // hoisted function declaration below, so it's available when this effect runs post-mount.
  const autoSkippedRef = React.useRef(false);
  React.useEffect(function () {
    if (autoSkippedRef.current || !isPrefill) return;
    autoSkippedRef.current = true;
    if (validateStep1().length === 0) setStep(2);
  }, []);

  const _children = React.useState(
    initialData?.children && initialData.children.length > 0
      ? JSON.parse(JSON.stringify(initialData.children))
      : [makeChild(0)]
  );
  const children = _children[0];
  const setChildren = _children[1];
  // Lock the request type once the shelter starts filling step 2 (any child selection: Gender /
  // Year Level / School / a need). Before that, Back → NEXT re-asks the type; after, it doesn't.
  React.useEffect(
    function () {
      if (typeLockedRef.current) return;
      const started = (children || []).some(function (c) {
        return (
          c &&
          (c.gender ||
            c.yearLevel ||
            c.schoolName ||
            (c.fees && c.fees.length > 0) ||
            c.uniforms ||
            c.shoes ||
            c.laptop)
        );
      });
      if (started) typeLockedRef.current = true;
    },
    [children]
  );

  // Seasonal section blocks (Manage → Section Availability). Once the config
  // loads, strip any currently-blocked sections from a reopened draft: the tick
  // is removed, the section's entries are discarded, and ONE modal explains
  // why (persisted at the next save). Runs once per mount.
  const _blocks = useSectionBlocks();
  const sectionBlocks = _blocks.blocks;
  const sectionBlocksLoaded = _blocks.isLoaded;
  const blocksAppliedRef = React.useRef(false);
  React.useEffect(
    function () {
      if (!sectionBlocksLoaded || blocksAppliedRef.current) return;
      blocksAppliedRef.current = true;
      setChildren(function (prev) {
        const res = stripBlockedSections(prev, sectionBlocks);
        if (res.removed.length === 0) return prev;
        const lines = res.removed.map(function (b) {
          return `${sectionBlockLabel(b.section)}: ${b.message}`;
        });
        alertDialog({
          title: "Some selections are currently unavailable",
          message: `${lines.join("\n\n")}\n\nThese selections have been removed from this draft.`,
        });
        return res.children;
      });
    },
    [sectionBlocksLoaded]
  );

  const _aci = React.useState(0);
  const activeChildIdx = _aci[0];
  const setActiveChildIdx = _aci[1];

  const _ccEmails = React.useState(initialData ? initialData.ccEmails || [] : []);
  const ccEmails = _ccEmails[0];
  const setCcEmails = _ccEmails[1];

  const _inv = React.useState(Array(6).fill(null));
  const inv = _inv[0];
  const setInv = _inv[1];

  const _docs = React.useState(Array(6).fill(null));
  const docs = _docs[0];
  const setDocs = _docs[1];

  const _done = React.useState(false);
  const done = _done[0];
  const setDone = _done[1];

  const _filesOpen = React.useState(false);
  const filesOpen = _filesOpen[0];
  const setFilesOpen = _filesOpen[1];

  const _doneRef = React.useState("");
  const doneRef = _doneRef[0];
  const setDoneRef = _doneRef[1];

  const _submitting = React.useState(false);
  const submitting = _submitting[0];
  const setSubmitting = _submitting[1];

  const _submitErr = React.useState("");
  const submitErr = _submitErr[0];
  const setSubmitErr = _submitErr[1];
  const _showConfirm = React.useState(false);
  const showSubmitConfirm = _showConfirm[0];
  const setShowSubmitConfirm = _showConfirm[1];

  const _stepErr = React.useState([]);
  const stepErr = _stepErr[0];
  const setStepErr = _stepErr[1];

  const _shelterUsers = React.useState([]);
  const shelterUsers = _shelterUsers[0];
  const setShelterUsers = _shelterUsers[1];

  // The caseworker's submission note now has its OWN field (db-120) — it is NOT
  // a comment and no longer goes into the notes array (which is purely the
  // comment thread). Returns the sanitised note text, or null when empty.
  function caseworkerNoteValue() {
    return general.notes?.trim() ? sanitizeNote(general.notes) : null;
  }

  // ── Unsaved-changes guard for "Return to request list" ─────────────────────
  // "Return to request list" (onBack) discards the current step's in-progress
  // edits — only "Save Draft & Exit" (and the per-step auto-save) persists. To stop
  // silent data loss, fingerprint the persistable form state, snapshot it at
  // mount + after every save, and warn before leaving if it has drifted. (Files
  // can't be JSON-serialised, so they're fingerprinted by present/absent slot.)
  function formFingerprint() {
    return JSON.stringify({
      g: general,
      c: children,
      cc: ccEmails,
      iv: inv.map(Boolean),
      dc: docs.map(Boolean),
    });
  }
  const lastSavedRef = React.useRef(null);
  React.useEffect(function () {
    if (lastSavedRef.current === null) lastSavedRef.current = formFingerprint();
  }, []);
  function markSaved() {
    lastSavedRef.current = formFingerprint();
  }
  async function handleReturn() {
    if (lastSavedRef.current !== null && formFingerprint() !== lastSavedRef.current) {
      const ok = await confirmDialog({
        title: "Unsaved changes",
        message:
          'You have unsaved changes. Use "Save Draft & Exit" to keep them. Discard your changes and return to the request list?',
        confirmLabel: "Discard & return",
        cancelLabel: "Keep editing",
        danger: true,
      });
      if (!ok) return;
    }
    onBack();
  }

  // "Save Draft & Exit" — persists the whole form as a draft, then leaves.
  // Lives in each step's footer button row (moved out of the teal header,
  // John's call 5 July 2026). Bypasses the unsaved-changes guard on purpose:
  // the save IS the keep.
  function saveDraftAndExit() {
    if (onSaveDraft)
      onSaveDraft({
        shelterRef: general.clientRef,
        phone: general.phone,
        mobile: general.mobile,
        deliveryAddress: general.delivery,
        caseworkerNote: caseworkerNoteValue(),
        notes: [],
        children: children,
        invoices: inv,
        docs: docs,
        _exitAfterSave: true,
      });
    onBack();
  }

  // Register auto-save for session timeout
  React.useEffect(function () {
    if (!saveDraftRef) return;
    saveDraftRef.current = function () {
      if (onSaveDraft) {
        onSaveDraft({
          shelterRef: general.clientRef,
          phone: general.phone,
          mobile: general.mobile,
          deliveryAddress: general.delivery,
          caseworkerNote: caseworkerNoteValue(),
          notes: [],
          children: children,
          invoices: inv,
          docs: docs,
        });
        markSaved();
      }
    };
    return function () {
      if (saveDraftRef) saveDraftRef.current = null;
    };
  });

  // Load shelter colleagues for CC selector via SECURITY DEFINER RPC so we
  // only expose safe fields (display_name, user_email) — internal_notes and
  // other admin-only profile columns stay hidden from shelter users.
  React.useEffect(
    function () {
      if (!user.shelterUuid) return;
      // Scope to the active shelter so a multi-service user only sees
      // colleagues at the service this request is for.
      getShelterColleagues(user.shelterUuid).then(function (data) {
        if (data && data.length > 0) setShelterUsers(data);
      });
    },
    [user.id, user.shelterUuid]
  );

  // ── Child helpers ──────────────────────────────────────────────────────────
  function addChild() {
    if (children.length >= 5) return;
    const newChildren = children
      .map(function (c) {
        return JSON.parse(JSON.stringify(c));
      })
      .concat([makeChild(children.length)]);
    setChildren(newChildren);
    setActiveChildIdx(newChildren.length - 1);
  }
  function removeChild(idx) {
    if (children.length <= 1) return;
    const newChildren = children.filter(function (_, i) {
      return i !== idx;
    });
    setChildren(newChildren);
    setActiveChildIdx(Math.min(activeChildIdx, newChildren.length - 1));
  }
  function updateChild(idx, updated) {
    setChildren(
      children.map(function (c, i) {
        return i === idx ? updated : JSON.parse(JSON.stringify(c));
      })
    );
    if (stepErr && stepErr.length > 0) setStepErr([]);
  }

  function setGen(f, v) {
    setGeneral(function (p) {
      return Object.assign({}, p, { [f]: v });
    });
    if (stepErr && stepErr.length > 0) setStepErr([]);
  }

  // ── Step 1 validation ──────────────────────────────────────────────────────
  function validateStep1() {
    const errs = [];
    if (!general.clientRef?.trim()) errs.push("Client reference is required");
    else if (general.clientRef.trim().length < 5)
      errs.push("Client reference must be at least 5 characters");
    if (!general.phone?.trim() && !general.mobile?.trim())
      errs.push("Enter at least one contact number (mobile or phone)");
    if (general.mobile?.trim() && !isValidAUPhone(general.mobile))
      errs.push("Contact mobile must be a valid Australian number");
    if (general.phone?.trim() && !isValidAUPhone(general.phone))
      errs.push("Contact phone must be a valid Australian number");
    if (!general.delivery?.trim() || general.delivery === "__OTHER__")
      errs.push("Delivery address is required");
    else if (!/\d{4}/.test(general.delivery)) {
      const regAddrs = (user.deliveryAddresses || []).map(function (a) {
        return [a.address_name, a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
          .filter(Boolean)
          .join(", ");
      });
      if (regAddrs.indexOf(general.delivery) === -1)
        errs.push("Please include a 4-digit postcode in the delivery address");
    }
    return errs;
  }

  // ── Step 2 validation ──────────────────────────────────────────────────────
  function validateStep2() {
    const errs = [];
    let hasAnyNeed = false;
    children.forEach(function (child, i) {
      const n = `Child ${i + 1}`;
      if (!child.gender) errs.push(`${n}: gender is required`);
      if (!child.yearLevel) errs.push(`${n}: year level is required`);
      if (!child.schoolName?.trim()) errs.push(`${n}: school name is required`);
      else if (child.schoolConfirmed === false)
        errs.push(`${n}: school name — select from the list, or choose "Other" and type the name`);
      // Backstop for the UI rule: TAFE can only be the school when Year Level = TAFE.
      if (child.schoolName === "TAFE" && child.yearLevel !== "TAFE")
        errs.push(`${n}: TAFE can only be the school when Year Level is TAFE`);
      const childHasNeed = child.fees.length > 0 || child.uniforms || child.shoes || child.laptop;
      if (!childHasNeed) errs.push(`${n}: at least one need must be selected`);
      if (childHasNeed) hasAnyNeed = true;

      // Fees — skip entries with no amount and no description (cleared fees)
      child.fees.forEach(function (f, fi) {
        const label = `${n} ${f.feeType || `fee ${fi + 1}`}`;
        const hasAmount = f.feeAmount && parseFloat(f.feeAmount) > 0;
        const hasDesc = f.feeDesc && f.feeDesc.trim().length > 0;
        if (!hasAmount && !hasDesc) return;
        if (hasAmount && !hasDesc) errs.push(`${label}: description is required`);
        if (!hasAmount && hasDesc) errs.push(`${label}: amount is required`);
        if (f.feeType === "Camp Fees" && hasAmount && !f.brochure)
          errs.push(`${n} Camp Fees: brochure upload is required`);
        if (f.feeType === "Resource Fees" && hasAmount && !f.brochure)
          errs.push(`${n} Resource Fees: school letter upload is required`);
        if (f.feeType === "Device Hire" && hasAmount && !f.brochure)
          errs.push(`${n} Device Hire: device hire agreement upload is required`);
        if (f.feeType === "Device Hire" && hasAmount && child.laptop) {
          errs.push(`${n}: Device Hire and Device cannot both be requested. Remove one.`);
        }
      });

      // Shoes
      if (child.shoes) {
        const s = child.shoes;
        const hasJ = (s.jQty || 0) > 0;
        const hasL = (s.lQty || 0) > 0;
        const hasJCost = parseFloat(s.jCost) > 0;
        const hasLCost = parseFloat(s.lCost) > 0;
        const sc = standardCosts || DEFAULT_STANDARD_COSTS;
        const jMax = isPrimary(child.yearLevel)
          ? sc.joggers_primary || 60
          : sc.joggers_secondary || 80;
        const lMax = sc.leather_shoes || 100;
        if (hasJ && !hasJCost) errs.push(`${n} shoes: joggers cost is required`);
        if (!hasJ && hasJCost)
          errs.push(`${n} shoes: joggers qty is required when cost is entered`);
        if (hasJ && parseFloat(s.jCost) > jMax)
          errs.push(`${n} shoes: joggers cost exceeds maximum of $${jMax}`);
        if (hasL && !hasLCost) errs.push(`${n} shoes: leather shoes cost is required`);
        if (!hasL && hasLCost)
          errs.push(`${n} shoes: leather shoes qty is required when cost is entered`);
        if (hasL && parseFloat(s.lCost) > lMax)
          errs.push(`${n} shoes: leather shoes cost exceeds maximum of $${lMax}`);
        if (!hasJ && !hasL && !hasJCost && !hasLCost)
          errs.push(`${n} shoes: at least one shoe type must have a quantity`);
      }

      // Uniforms
      if (child.uniforms) {
        if (!child.uniforms.supplier) errs.push(`${n} uniforms: please select a supplier`);
        const isSL = child.uniforms.supplier === "School Locker";
        if (isSL) {
          if (!child.uniforms.mobile?.trim())
            errs.push(
              `${n} uniforms: School Locker mobile number is required for SMS pickup notification`
            );
          else if (!isValidAUPhone(child.uniforms.mobile))
            errs.push(`${n} uniforms: School Locker mobile must be a valid Australian number`);
        }
        const uniItems = child.uniforms.items || {};
        const customItems = child.uniforms.customItems || [];
        let hasAnyUniItem = false;
        const allUniKeys = [].concat(UNIFORM_ITEMS, WINTER_ITEMS);
        // School Locker rows are all-or-nothing across SKU / size / cost / qty:
        // if any one of the four is filled, all four are required.
        function slRowState(it) {
          const hasQty = (it.qty || 0) > 0;
          const hasCost = parseFloat(it.cost) > 0;
          const hasSku = !!it.sku?.trim();
          const hasSize = !!it.size?.trim();
          const anyFilled = hasQty || hasCost || hasSku || hasSize;
          const missing = [];
          if (anyFilled) {
            if (!hasSku) missing.push("SKU");
            if (!hasSize) missing.push("size");
            if (!hasCost) missing.push("cost");
            if (!hasQty) missing.push("quantity");
          }
          return { complete: anyFilled && missing.length === 0, missing: missing };
        }
        allUniKeys.forEach(function (k) {
          const it = uniItems[k];
          if (!it) return;
          if (isSL) {
            const r = slRowState(it);
            if (r.missing.length > 0)
              errs.push(
                `${n} uniforms — ${k}: School Locker items need SKU, size, cost and quantity (missing ${r.missing.join(", ")})`
              );
            if (r.complete) hasAnyUniItem = true;
            return;
          }
          const hasQty = (it.qty || 0) > 0;
          const hasCost = parseFloat(it.cost) > 0;
          if (hasQty && !hasCost) errs.push(`${n} uniforms — ${k}: cost required`);
          if (!hasQty && hasCost) errs.push(`${n} uniforms — ${k}: quantity required`);
          if (hasQty && hasCost) hasAnyUniItem = true;
        });
        customItems.forEach(function (it, ci) {
          if (!it || (!it.name && !(it.qty > 0) && !parseFloat(it.cost) && !it.sku && !it.size))
            return;
          if (isSL) {
            const r = slRowState(it);
            if (r.missing.length > 0)
              errs.push(
                `${n} uniforms — custom item ${ci + 1}: School Locker items need SKU, size, cost and quantity (missing ${r.missing.join(", ")})`
              );
            if (r.complete) hasAnyUniItem = true;
            return;
          }
          const hasName = !!it.name?.trim();
          const hasQty = (it.qty || 0) > 0;
          const hasCost = parseFloat(it.cost) > 0;
          // An additional item with a cost/qty but no name is not a valid line
          // item — block submit so the name can't be left blank (or removed
          // after the cost/qty were entered).
          if ((hasQty || hasCost) && !hasName)
            errs.push(`${n} uniforms — additional item ${ci + 1}: item name is required`);
          if (hasQty && !hasCost)
            errs.push(`${n} uniforms — additional item ${ci + 1}: cost required`);
          if (!hasQty && hasCost)
            errs.push(`${n} uniforms — additional item ${ci + 1}: quantity required`);
          if (hasName && hasQty && hasCost) hasAnyUniItem = true;
        });
        if (child.uniforms.supplier && !hasAnyUniItem)
          errs.push(`${n} uniforms: at least one item with quantity and cost is required`);
        // 3-day supply rule
        function uniQty(k) {
          const it = uniItems[k];
          return it ? parseInt(it.qty, 10) || 0 : 0;
        }
        const dresses = uniQty("Dress");
        const tops = uniQty("Formal Shirt") + uniQty("Sports/Polo Shirt");
        const bottoms =
          uniQty("Formal Shorts") +
          uniQty("Formal Skirt") +
          uniQty("Sports Shorts") +
          uniQty("Sports Skirt/Skort");
        const topsMax = 3 - dresses;
        const bottomsMax = 3 - dresses;
        if (tops > topsMax)
          errs.push(
            n +
              " uniforms: too many tops (max " +
              topsMax +
              (dresses > 0 ? `, reduced by ${dresses} dress(es)` : "") +
              ")"
          );
        if (bottoms > bottomsMax)
          errs.push(
            n +
              " uniforms: too many bottoms (max " +
              bottomsMax +
              (dresses > 0 ? `, reduced by ${dresses} dress(es)` : "") +
              ")"
          );
        if (uniQty("Sun-Safe Everyday Hat") > 1)
          errs.push(`${n} uniforms: max 1 sun-safe everyday hat`);
        if (uniQty("Sports Hat") > 1) errs.push(`${n} uniforms: max 1 sports hat`);
        if (uniQty("Socks") > 3) errs.push(`${n} uniforms: max 3 pairs of socks`);
        if (uniQty("Tie") > 1) errs.push(`${n} uniforms: max 1 tie`);
        if (uniQty("Backpack") > 1) errs.push(`${n} uniforms: max 1 backpack`);
        if (uniQty("Track Jacket") > 1) errs.push(`${n} uniforms: max 1 track jacket`);
        if (uniQty("Track Pants") > 1) errs.push(`${n} uniforms: max 1 track pants`);
        if (uniQty("Jumper") > 1) errs.push(`${n} uniforms: max 1 jumper`);
      }

      // Laptop/Device
      if (child.laptop) {
        const l = child.laptop;
        const isTafe = child.yearLevel === "TAFE";
        if (!l.deviceType) errs.push(`${n} device: device type is required`);
        if (!l.whyNeeded?.trim()) errs.push(`${n} device: please explain why the device is needed`);
        if (!l.byod)
          errs.push(
            n +
              " device: " +
              (isTafe
                ? "enrolment confirmation must be uploaded"
                : "BYOD policy document must be uploaded")
          );
        if (!isTafe && !l.hasSchoolDevice)
          errs.push(`${n} device: please confirm the school has a BYOD policy`);
        if (isTafe && !l.tafeCourse?.trim())
          errs.push(`${n} device: course name is required for TAFE`);
        if (isTafe && !l.tafeStartDate?.trim())
          errs.push(`${n} device: course start date is required for TAFE`);
        if (isTafe && !l.tafeLength?.trim())
          errs.push(`${n} device: length of course is required for TAFE`);
        if (!l.internetSafety)
          errs.push(`${n} device: internet safety acknowledgement is required`);
        if (!l.termsAccepted) errs.push(`${n} device: terms and conditions must be accepted`);
        if (!l.estimateAck)
          errs.push(
            `${n} device: the estimate-only / no purchase authority acknowledgement is required`
          );
        if (
          showDeviceDelivery &&
          (!l.deliveryAddress?.trim() || l.deliveryAddress === "__OTHER__")
        ) {
          errs.push(`${n} device: delivery address is required`);
        } else if (showDeviceDelivery && !/\d{4}/.test(l.deliveryAddress)) {
          const regAddrs2 = (user.deliveryAddresses || []).map(function (a) {
            return [
              a.address_name,
              a.street,
              a.street2,
              a.suburb_city || a.suburb,
              a.state,
              a.postcode,
            ]
              .filter(Boolean)
              .join(", ");
          });
          if (regAddrs2.indexOf(l.deliveryAddress) === -1)
            errs.push(`${n} device: please include a 4-digit postcode in the delivery address`);
        }
      }
    });
    if (!hasAnyNeed) errs.push("At least one child must have a need selected");
    return errs;
  }

  // ── Step navigation ────────────────────────────────────────────────────────
  function goToStep2() {
    const errs = validateStep1();
    if (errs.length > 0) {
      setStepErr(errs);
      // Auto-jump to first child with errors
      const firstErrChild = errs
        .map(function (e) {
          const m = e.match(/^Child (\d+)/);
          return m ? parseInt(m[1], 10) - 1 : -1;
        })
        .filter(function (i) {
          return i >= 0;
        })[0];
      if (firstErrChild !== undefined) setActiveChildIdx(firstErrChild);
      window.scrollTo(0, 0);
      return;
    }
    setStepErr([]);
    // db-154 (stock-top-ups): once the top-up type is chosen, advance straight to the top-up step 2
    // — bypassing the request-type modal AND the draft-save below (top-ups use ephemeral staging, no
    // Draft row). Covers a Back → NEXT after the choice; the first entry comes from the modal button.
    if (requestType === "stock_top_up") {
      setStep(2);
      window.scrollTo(0, 0);
      return;
    }
    // Phase-0: on the FIRST advance from step 1 of a new request, show the request-type modal —
    // but ONLY for a service that HOLDS STOCK (db-153). A service that doesn't hold stock never
    // does a Stock Top-up, so it skips the chooser and falls straight through as a Fit-out request
    // (13 Sep 2026). The modal's "Fit out Children" button re-enters here with typeAckRef set, so
    // this fires at most once; the "Stock Top-up" button advances itself (above) without re-entering.
    const offerChooser = user.stockHeld === true;
    if (!typeLockedRef.current && !typeAckRef.current && offerChooser) {
      setTypeModal(true);
      return;
    }
    typeAckRef.current = false;
    // Auto-save draft on step transition
    if (onSaveDraft) {
      onSaveDraft({
        shelterRef: general.clientRef,
        phone: general.phone,
        mobile: general.mobile,
        deliveryAddress: general.delivery,
        caseworkerNote: caseworkerNoteValue(),
        notes: [],
        children: children,
        invoices: inv,
        docs: docs,
      });
      markSaved();
    }
    setStep(2);
    window.scrollTo(0, 0);
  }

  function goToStep3() {
    const errs = validateStep2();
    if (errs.length > 0) {
      setStepErr(errs);
      // Auto-jump to first child with errors
      const firstErrChild = errs
        .map(function (e) {
          const m = e.match(/^Child (\d+)/);
          return m ? parseInt(m[1], 10) - 1 : -1;
        })
        .filter(function (i) {
          return i >= 0;
        })[0];
      if (firstErrChild !== undefined) setActiveChildIdx(firstErrChild);
      window.scrollTo(0, 0);
      return;
    }
    setStepErr([]);
    // Auto-save draft on step transition
    if (onSaveDraft) {
      onSaveDraft({
        shelterRef: general.clientRef,
        phone: general.phone,
        mobile: general.mobile,
        deliveryAddress: general.delivery,
        caseworkerNote: caseworkerNoteValue(),
        notes: [],
        children: children,
        invoices: inv,
        docs: docs,
      });
      markSaved();
    }
    setStep(3);
    window.scrollTo(0, 0);
  }

  function goBack() {
    setStepErr("");
    setStep(function (s) {
      return s - 1;
    });
    window.scrollTo(0, 0);
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitting(true);
    setSubmitErr("");
    // Final combined validation
    const errs = validateStep1().concat(validateStep2());
    if (errs.length > 0) {
      setSubmitErr(errs.join(" · "));
      setSubmitting(false);
      return;
    }
    const year = new Date().getFullYear();
    async function nextRefNo() {
      // Use DB function (SECURITY DEFINER) to see ALL requests regardless of RLS
      const rpcRes = await nextRefNoRpc();
      if (!rpcRes.error && rpcRes.data) return rpcRes.data;
      // Fallback: client-side query (only sees own requests due to RLS)
      const topRef = await getMaxRefNoForYear(year);
      let lastNum = 0;
      if (topRef) {
        const parts = topRef.split("-");
        lastNum = parseInt(parts[parts.length - 1], 10) || 0;
      }
      return `${year}-${String(lastNum + 1).padStart(5, "0")}`;
    }
    const refNo = await nextRefNo();
    // Stage 2: shelter address lives on the shelters table now; pull a fresh
    // copy by shelter_id (falls back to user.shelterAddress already loaded at login).
    let shelterAddress = user.shelterAddress || "";
    if (user.shelterUuid) {
      try {
        const sh = await getShelterById(user.shelterUuid);
        if (sh) {
          const fresh = [
            sh.postal_line1,
            sh.postal_line2,
            sh.postal_suburb_city,
            sh.postal_state,
            sh.postal_postcode,
          ]
            .filter(Boolean)
            .join(", ");
          if (fresh) shelterAddress = fresh;
        }
      } catch (_e) {}
    }
    const regVals = (user.deliveryAddresses || []).map(function (a) {
      return [a.address_name, a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
        .filter(Boolean)
        .join(", ");
    });
    const isNonReg =
      general.delivery && regVals.length > 0 && regVals.indexOf(general.delivery) === -1;
    // Route the Draft→Submitted transition through the guarded reducer (mirror of
    // db-8). A brand-new request has no prior status (treated as Draft); editing a
    // draft passes its current status. Either way a Draft advances to Submitted.
    const submitStatus = nextStatusOnSubmit(initialData?.status);
    const reqData = {
      refNo: refNo,
      shelterName: sanitizeText(user.shelterName),
      shelterCode: user.shelterCode || "",
      state: user.state,
      status: submitStatus,
      date: new Date().toISOString().slice(0, 10),
      shelterAddress: sanitizeText(shelterAddress),
      shelterRef: sanitizeText(general.clientRef),
      phone: sanitizePhone(general.phone),
      mobile: general.mobile ? sanitizePhone(general.mobile) : "",
      deliveryAddress: sanitizeText(general.delivery),
      contactName: sanitizeText(contactNameForUser(user)),
      nonRegisteredAddress: isNonReg,
      // The caseworker's submission note has its OWN field now (db-120) — it is
      // NOT a comment, so it no longer seeds the notes/comment thread.
      caseworkerNote: caseworkerNoteValue(),
      notes: [],
      children: children,
      invoices: inv,
      docs: docs,
      ccEmails: ccEmails,
      shelterEmail: user.email || "",
    };
    if (onSaveDraft) {
      if (!user.shelterUuid) {
        alertDialog({
          message:
            "We couldn't determine your DFV Service. Please log out and back in, then try again.",
        });
        return;
      }
      let finalRefNo = draftRefNo && !draftRefNo.startsWith("DRAFT") ? draftRefNo : refNo;
      // Verified-address model: registered match → id; a picked one-off →
      // dedupe-or-create an UNVERIFIED structured delivery_addresses row + link its
      // id; freehand/failure → flat override text. Never blocks the submit.
      const delivery1 = await resolveDeliveryForSubmit({
        deliveryAddress: reqData.deliveryAddress,
        deliveryParts: general.deliveryParts,
        shelterId: user.shelterUuid,
        deliveryAddresses: user?.deliveryAddresses,
      });
      const row = {
        ref_no: finalRefNo,
        // Task #DB-1 Phase 1: shelter_id is now the shelters.id surrogate FK;
        // the submitter goes in submitted_by.
        shelter_id: user.shelterUuid,
        submitted_by: user.id,
        shelter_name: user.shelterName,
        state: user.state,
        status: submitStatus,
        date: reqData.date,
        shelter_ref: reqData.shelterRef,
        shelter_address: shelterAddress,
        phone: reqData.phone,
        mobile: reqData.mobile,
        delivery_address_id: delivery1.delivery_address_id,
        delivery_address_override: delivery1.delivery_address_override,
        non_registered_address: isNonReg,
        notes: reqData.notes,
        caseworker_note: reqData.caseworkerNote,
        // Stage 1: children no longer written here — saved via save_request_children RPC after insert/update
        invoices: reqData.invoices,
        docs: reqData.docs,
        // shelter_code intentionally NOT written (identity Pass 2): the request
        // links to its shelter by shelter_id only. The displayed code comes from
        // the requests_view shelter join (db-85). Column dropped in db-86.
        contact_name: reqData.contactName || "",
        cc_emails: ccEmails,
        shelter_email: user.email || "",
        ...requestStamp(user),
      };
      let effectiveDraftId = draftId || (getPendingDraftId ? getPendingDraftId() : null);
      // Retry up to 3 times if ref_no collision occurs.
      // upsert_request_atomic does request INSERT/UPDATE + save_request_children
      // in a single Postgres transaction — if children fail, the request rolls back.
      let res = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) {
          // Re-generate ref_no on retry
          finalRefNo = await nextRefNo();
          row.ref_no = finalRefNo;
          reqData.refNo = finalRefNo;
        }
        res = await upsertRequestAtomic(effectiveDraftId || null, row, children || []);
        if (!res.error) break;
        const errMsgA = res.error?.message || "";
        // Draft id not found — fall back to insert and retry
        if (effectiveDraftId && errMsgA.indexOf("not found") >= 0) {
          effectiveDraftId = null;
          continue;
        }
        // Non-collision error — stop retrying
        if (errMsgA.indexOf("requests_ref_no_key") === -1) break;
      }
      if (res.error) {
        setSubmitErr(`Submit failed: ${res.error.message}`);
        setSubmitting(false);
        return;
      }
      // res.data is the requests_view row (already includes children)
      const dbRowForParentA = res.data;
      if (res.data && onSaveDraft)
        onSaveDraft(
          Object.assign({}, reqData, {
            refNo: finalRefNo,
            status: submitStatus,
            id: res.data.id,
            _alreadySaved: true,
            _dbRow: dbRowForParentA,
          })
        );
      createTask("new_request", {
        requestId: res.data?.id,
        refNo: finalRefNo,
        shelterName: user.shelterName,
        shelterCode: user.shelterCode || "",
        state: user.state,
      });
      // Confirmation email to the submitting caseworker ("we've received it").
      if (user.email) {
        sendEmail("request_received", {
          requestId: res.data?.id,
          refNo: finalRefNo,
          shelterName: user.shelterName,
          shelterEmail: user.email,
        });
      }
      // "Other" school alert (John, 9 Aug 2026): if any child's school was free-typed
      // (not in the schools table), email the System Alert address so staff can add it.
      // Detected fresh at submit (survives drafts); best-effort, never blocks the flow.
      const otherCandidates = otherSchoolCandidates(children);
      if (otherCandidates.length > 0) {
        listExistingSchoolNames(
          otherCandidates.map(function (c) {
            return c.lookupName;
          }),
          user.state
        ).then(function (existing) {
          const others = otherSchoolsToAlert(otherCandidates, existing);
          if (others.length > 0) {
            sendEmail("school_other_alert", {
              requestId: res.data?.id,
              refNo: finalRefNo,
              shelterName: user.shelterName,
              schools: others.map(function (o) {
                return { child: o.childLabel, schoolName: o.schoolName };
              }),
            });
          }
        });
      }
      logAudit("request_submitted", {
        request_id: res.data?.id,
        ref: finalRefNo,
        shelter: user.shelterCode,
        by: user.shelterName,
      });
      setDoneRef(finalRefNo);
      setDone(true);
      setSubmitting(false);
      return;
    }
    setDoneRef(refNo);
    setDone(true);
    setSubmitting(false);
  }

  // ── Stock Top-up submit (ISOLATED) ───────────────────────────────────────────
  // Copies the request ENVELOPE from the fit-out submit (ref, delivery, contact) —
  // that plumbing is shared request infrastructure — but has NO children and writes
  // the note + lines via submit_stock_top_up_atomic. The fit-out handleSubmit above
  // is deliberately left untouched. Returns { error } on failure so StockTopUpStep
  // can surface it; on success it shows the wizard's shared "done" screen.
  async function handleTopUpSubmit(lines, note) {
    // The top-up flow can skip step 1 (Phase 5 prefill auto-advance), so re-validate the
    // step-1 envelope here before submitting — jump back to step 1 if anything's missing.
    const step1Errs = validateStep1();
    if (step1Errs.length > 0) {
      setStep(1);
      setStepErr(step1Errs);
      return { error: step1Errs.join(". ") };
    }
    if (!user.shelterUuid) {
      return {
        error:
          "We couldn't determine your DFV Service. Please log out and back in, then try again.",
      };
    }
    // A staff-prepared top-up may have been cancelled by Zephyr while this page was open —
    // re-check it is still waiting before submitting (the RPC alone would still create it).
    if (initialData?.prefillId) {
      const block = prefillSubmitBlock(await getPrefillStatus(initialData.prefillId));
      if (block) return { error: block };
    }
    const year = new Date().getFullYear();
    async function genRef() {
      const rpcRes = await nextRefNoRpc();
      if (!rpcRes.error && rpcRes.data) return rpcRes.data;
      const maxRef = await getMaxRefNoForYear(year);
      const lastNum = maxRef ? parseInt(String(maxRef).split("-")[1], 10) : 0;
      return `${year}-${String((lastNum || 0) + 1).padStart(5, "0")}`;
    }
    let shelterAddress = user.shelterAddress || "";
    try {
      const sh = await getShelterById(user.shelterUuid);
      if (sh) {
        const fresh = [
          sh.postal_line1,
          sh.postal_line2,
          sh.postal_suburb_city,
          sh.postal_state,
          sh.postal_postcode,
        ]
          .filter(Boolean)
          .join(", ");
        if (fresh) shelterAddress = fresh;
      }
    } catch (_e) {}
    const regVals = (user.deliveryAddresses || []).map(function (a) {
      return [a.address_name, a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
        .filter(Boolean)
        .join(", ");
    });
    const isNonReg =
      general.delivery && regVals.length > 0 && regVals.indexOf(general.delivery) === -1;
    const delivery1 = await resolveDeliveryForSubmit({
      deliveryAddress: sanitizeText(general.delivery),
      deliveryParts: general.deliveryParts,
      shelterId: user.shelterUuid,
      deliveryAddresses: user?.deliveryAddresses,
    });
    let finalRefNo = await genRef();
    const request = {
      ref_no: finalRefNo,
      shelter_id: user.shelterUuid,
      shelter_name: user.shelterName,
      state: user.state,
      status: nextStatusOnSubmit(initialData?.status),
      date: new Date().toISOString().slice(0, 10),
      shelter_ref: sanitizeText(general.clientRef),
      shelter_address: sanitizeText(shelterAddress),
      phone: sanitizePhone(general.phone),
      mobile: general.mobile ? sanitizePhone(general.mobile) : "",
      delivery_address_id: delivery1.delivery_address_id,
      delivery_address_override: delivery1.delivery_address_override,
      non_registered_address: isNonReg,
      notes: [],
      invoices: [],
      docs: [],
      contact_name: sanitizeText(contactNameForUser(user)) || "",
      cc_emails: ccEmails,
      shelter_email: user.email || "",
      ...requestStamp(user),
      caseworker_note: null,
      note: note || null,
      // Phase 5: when the service submits a staff-prepared prefill, the RPC marks it
      // consumed atomically (db-164). Absent (undefined → omitted) for a normal top-up.
      prefill_id: initialData?.prefillId || undefined,
    };
    let res = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        finalRefNo = await genRef();
        request.ref_no = finalRefNo;
      }
      res = await submitStockTopUp(request, lines);
      if (!res.error) break;
      if ((res.error.message || "").indexOf("requests_ref_no_key") === -1) break;
    }
    if (res.error) return { error: `Submit failed: ${res.error.message}` };
    logAudit("request_submitted", {
      request_id: res.data?.id,
      ref: finalRefNo,
      shelter: user.shelterCode,
      by: user.shelterName,
      request_type: "stock_top_up",
    });
    // The approval task for the Top-up Task List (its own assignable queue, excluded
    // from the Responders + Pickers lists). One per submitted top-up.
    createTask("top_up_approval", {
      requestId: res.data?.id,
      refNo: finalRefNo,
      shelterName: user.shelterName,
      shelterCode: user.shelterCode,
      state: user.state,
    });
    if (user.email) {
      sendEmail("request_received", {
        requestId: res.data?.id,
        refNo: finalRefNo,
        shelterName: user.shelterName,
        shelterEmail: user.email,
      });
    }
    setDoneRef(finalRefNo);
    setDone(true);
    return {};
  }

  // ── Totals ─────────────────────────────────────────────────────────────────
  const childFeeTotal = children.reduce(function (s, c) {
    return (
      s +
      (c.fees || []).reduce(function (fs, f) {
        return fs + (parseFloat(f.feeAmount) || 0);
      }, 0)
    );
  }, 0);
  const childShoeTotal = children.reduce(function (s, c) {
    if (!c.shoes) return s;
    return (
      s +
      (c.shoes.jQty || 0) * (parseFloat(c.shoes.jCost) || 0) +
      (c.shoes.lQty || 0) * (parseFloat(c.shoes.lCost) || 0)
    );
  }, 0);
  const childUniTotal = children.reduce(function (s, c) {
    if (!c.uniforms) return s;
    return s + calcUniItems([c.uniforms]);
  }, 0);
  const childLapTotal = children.reduce(function (s, c) {
    if (!c.laptop) return s;
    const cost =
      c.laptop.estimatedCost ||
      (c.laptop.deviceType
        ? String(deviceCost(standardCosts || DEFAULT_STANDARD_COSTS, c.laptop.deviceType))
        : "0");
    return s + (parseFloat(cost) || 0);
  }, 0);
  const grand = childFeeTotal + childShoeTotal + childUniTotal + childLapTotal;

  // ── Done ───────────────────────────────────────────────────────────────────
  if (done) return React.createElement(SubmittedScreen, { refNo: doneRef, onBack: onBack });

  // ── Chrome (header + step bar) ─────────────────────────────────────────────
  const header = (
    <div style={{ background: "#00695C", color: "#FFFFFF", padding: "14px 24px" }}>
      <button
        onClick={handleReturn}
        style={{
          background: "none",
          border: "none",
          color: "rgba(255,255,255,0.75)",
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
          padding: 0,
          marginBottom: 8,
        }}
      >
        {GLYPH.arrowLeft} Return to request list
      </button>
      <div
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>New Request</h1>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
            {user.shelterName}
            {user.shelterCode ? ` · ${user.shelterCode}` : ""} - {user.state}
          </div>
        </div>
        {/* Centred, vertically-stacked help links (Preparation checklist on top).
            Hidden on the Stock Top-up path — those are fit-out concerns. */}
        {requestType !== "stock_top_up" && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, opacity: 0.9 }}>
              Preparation checklist
              <HelpButton helpKey="request_worksheet" user={user} light={true} />
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, opacity: 0.9 }}>
              Invoice requirements
              <HelpButton helpKey="invoices_payment" user={user} light={true} />
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // ── Error banner ───────────────────────────────────────────────────────────
  // Parse errors into a per-child map for field highlighting
  const childErrMap = {};
  (stepErr || []).forEach(function (err) {
    const m = err.match(/^Child (\d+)/);
    if (!m) return;
    const ci = parseInt(m[1], 10) - 1;
    if (!childErrMap[ci]) childErrMap[ci] = {};
    const e = err.toLowerCase();
    if (e.indexOf("gender") >= 0) childErrMap[ci].gender = true;
    if (e.indexOf("year level") >= 0) childErrMap[ci].yearLevel = true;
    if (e.indexOf("school name") >= 0) childErrMap[ci].schoolName = true;
    if (e.indexOf("at least one need") >= 0) childErrMap[ci].needs = true;
    if (
      e.indexOf("fee") >= 0 ||
      e.indexOf("stationery") >= 0 ||
      e.indexOf("camp") >= 0 ||
      e.indexOf("resource") >= 0 ||
      e.indexOf("textbook") >= 0
    )
      childErrMap[ci].fees = true;
    if (e.indexOf("uniform") >= 0 || e.indexOf("school locker") >= 0)
      childErrMap[ci].uniforms = true;
    if (e.indexOf("shoe") >= 0 || e.indexOf("jogger") >= 0 || e.indexOf("leather") >= 0)
      childErrMap[ci].shoes = true;
    if (
      e.indexOf("device") >= 0 ||
      e.indexOf("laptop") >= 0 ||
      e.indexOf("byod") >= 0 ||
      e.indexOf("tafe") >= 0 ||
      e.indexOf("internet safety") >= 0
    )
      childErrMap[ci].laptop = true;
  });

  const errBanner =
    stepErr && stepErr.length > 0 ? (
      <div
        style={{
          background: "#FFEBEE",
          border: "2px solid #C62828",
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          fontSize: 13,
          color: "#C62828",
          position: "sticky",
          top: 0,
          zIndex: 20,
          boxShadow: "0 4px 12px rgba(198,40,40,0.2)",
        }}
      >
        <strong style={{ fontSize: 14 }}>
          Please fix the items highlighted in red before continuing:
        </strong>
        <ul style={{ margin: "6px 0 0", paddingLeft: 20, lineHeight: 1.8 }}>
          {stepErr.map(function (e, i) {
            return e.trim() ? React.createElement("li", { key: i }, e.trim()) : null;
          })}
        </ul>
      </div>
    ) : null;

  // ── Summary helper for Step 3 ──────────────────────────────────────────────
  function SummaryRow(props) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "6px 0",
          borderBottom: `1px solid ${"#CFE0DE"}`,
          fontSize: 13,
        }}
      >
        <span style={{ color: "#607D79", fontWeight: 600 }}>{props.label}</span>
        <span style={{ color: "#1A2E2B" }}>{props.value}</span>
      </div>
    );
  }

  function ChildSummaryCard(props) {
    const child = props.child;
    const idx = props.idx;
    const needs = [];
    if (child.fees && child.fees.length > 0) needs.push("School Costs & Stationery");
    if (child.uniforms)
      needs.push(`Uniforms${child.uniforms.supplier ? ` (${child.uniforms.supplier})` : ""}`);
    if (child.shoes) needs.push("Shoes");
    if (child.laptop) needs.push("Device");
    // Calculate this child's total
    let childTotal = 0;
    childTotal += (child.fees || []).reduce(function (s, f) {
      return s + (parseFloat(f.feeAmount) || 0);
    }, 0);
    if (child.uniforms) childTotal += calcUniItems([child.uniforms]);
    if (child.shoes)
      childTotal +=
        (child.shoes.jQty || 0) * (parseFloat(child.shoes.jCost) || 0) +
        (child.shoes.lQty || 0) * (parseFloat(child.shoes.lCost) || 0);
    if (child.laptop) {
      const lapCost =
        child.laptop.estimatedCost ||
        (child.laptop.deviceType
          ? String(deviceCost(standardCosts || DEFAULT_STANDARD_COSTS, child.laptop.deviceType))
          : "0");
      childTotal += parseFloat(lapCost) || 0;
    }
    return (
      <div
        style={{
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 10,
          marginBottom: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#37474F",
            color: "white",
            padding: "10px 16px",
            fontWeight: 700,
            fontSize: 13,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {"Child " +
              (idx + 1) +
              (child.gender ? ` · ${child.gender}` : "") +
              (child.yearLevel ? ` · ${child.yearLevel}` : "")}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {childTotal > 0 && (
              <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
                {`${money(childTotal)}${child.laptop ? " est." : ""}`}
              </span>
            )}
            <button
              onClick={function () {
                setStep(2);
                setActiveChildIdx(idx);
                window.scrollTo(0, 0);
              }}
              style={{
                background: "none",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "white",
                borderRadius: 5,
                padding: "3px 10px",
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Edit
            </button>
          </div>
        </div>
        <div style={{ padding: "10px 16px" }}>
          {child.schoolName && (
            <div style={{ fontSize: 12, color: "#607D79", marginBottom: 6 }}>
              {child.schoolName}
            </div>
          )}
          {needs.length > 0 ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {needs.map(function (n) {
                return (
                  <span
                    key={n}
                    style={{
                      background: "#E0F2F1",
                      color: "#00695C",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {n}
                  </span>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#C62828" }}>No needs selected</div>
          )}
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
        minHeight: "100vh",
        background: "#F7F9F9",
      }}
    >
      {header}
      {typeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 28,
              maxWidth: 460,
              width: "100%",
              boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
              fontFamily: "inherit",
            }}
          >
            <h2 style={{ margin: "0 0 6px", fontSize: 20, color: "#1A2E2B" }}>
              What kind of request is this?
            </h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "#555" }}>
              Choose the type of request you'd like to make.
            </p>
            <button
              type="button"
              onClick={function () {
                typeAckRef.current = true;
                setTypeModal(false);
                goToStep2();
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "var(--color-zephyr-teal-light)",
                border: "1px solid #00897B",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 10,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <div style={{ fontWeight: 700, color: "#1A2E2B", fontSize: 14 }}>
                Fit out Children to get back to school
              </div>
              <div style={{ fontSize: 12, color: "#607D79" }}>
                The standard request — school costs, uniforms, shoes, devices.
              </div>
            </button>
            <button
              type="button"
              disabled={!topUpAvailable}
              onClick={function () {
                if (!topUpAvailable) return; // disabled ("Not available at the moment") — switch is off
                // db-154 Stock Top-up: lock the type immediately, skip the draft-save path (top-ups
                // use ephemeral staging — no Draft row), and advance to the top-up step 2. Step 1 was
                // already validated before this modal showed.
                setRequestType("stock_top_up");
                typeLockedRef.current = true;
                setTypeModal(false);
                setStep(2);
                window.scrollTo(0, 0);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: topUpAvailable ? "var(--color-zephyr-teal-light)" : "#F1F3F4",
                border: topUpAvailable ? "1px solid #00897B" : "1px solid #D3D8DA",
                borderRadius: 8,
                padding: "12px 14px",
                cursor: topUpAvailable ? "pointer" : "not-allowed",
                fontFamily: "inherit",
                opacity: topUpAvailable ? 1 : 0.7,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  color: topUpAvailable ? "#1A2E2B" : "#9AA6A4",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                Stock Top-up for our DFV Service
                {!topUpAvailable && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#8A6D3B",
                      background: "#FCF3D9",
                      border: "1px solid #EAD9A6",
                      borderRadius: 999,
                      padding: "1px 8px",
                    }}
                  >
                    Not available at the moment
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: topUpAvailable ? "#607D79" : "#A8B2B0" }}>
                Bulk supplies top-up (stationery, bags, hats, socks…).
              </div>
            </button>
          </div>
        </div>
      )}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "28px 20px" }}>
        <WizardStepBar
          step={step}
          step2Label={requestType === "stock_top_up" ? "Top-up Needs" : undefined}
        />
        {errBanner}

        {/* ── STEP 1: General Information ── */}
        {step === 1 && (
          <div>
            <Card>
              <TabGeneral
                d={general}
                set={setGen}
                deliveryAddresses={user.deliveryAddresses || []}
                user={user}
                shelterUsers={shelterUsers}
                ccEmails={ccEmails}
                setCcEmails={setCcEmails}
              />
            </Card>
            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "white",
                borderTop: `1px solid ${"#CFE0DE"}`,
                padding: "12px 20px",
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 10,
                zIndex: 10,
                borderRadius: "0 0 10px 10px",
              }}
            >
              <Btn variant="secondary" onClick={saveDraftAndExit}>
                Save Draft &amp; Exit
              </Btn>
              <Btn onClick={goToStep2} style={{ padding: "11px 32px", fontSize: 18 }}>
                NEXT {GLYPH.arrowRight}
              </Btn>
            </div>
          </div>
        )}

        {/* ── STEP 2: Children & Needs (standard fit-out request only) ── */}
        {step === 2 && requestType !== "stock_top_up" && (
          <div>
            <Card style={{ padding: "16px 20px 0" }}>
              <ChildTabBar
                childList={children}
                activeIdx={activeChildIdx}
                setActiveIdx={setActiveChildIdx}
                onAdd={addChild}
                onRemove={removeChild}
                maxChildren={5}
                childErrMap={childErrMap}
              />
              <div style={{ paddingBottom: 16 }}>
                <ChildTab
                  // Key by the active child so switching children REMOUNTS the
                  // subtree — otherwise per-child sections (fees/device/shoes/
                  // uniforms) that seed local state once via useState bleed the
                  // previous child's values onto the next (fields look filled but
                  // the new child's data is empty, so Save fails validation).
                  key={activeChildIdx}
                  child={children[activeChildIdx]}
                  onChange={function (updated) {
                    updateChild(activeChildIdx, updated);
                  }}
                  user={user}
                  sectionBlocks={sectionBlocks}
                  standardCosts={standardCosts}
                  generalDelivery={general.delivery}
                  refNo={initialData?.refNo || draftRefNo || "draft"}
                  fieldErrors={childErrMap[activeChildIdx] || {}}
                />
              </div>
            </Card>
            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "white",
                borderTop: `1px solid ${"#CFE0DE"}`,
                padding: "12px 20px",
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "0 0 10px 10px",
              }}
            >
              <Btn variant="secondary" onClick={goBack}>
                {GLYPH.arrowLeft} Back
              </Btn>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Btn variant="secondary" onClick={saveDraftAndExit}>
                  Save Draft &amp; Exit
                </Btn>
                {children.length < 5 && (
                  <Btn
                    variant="secondary"
                    onClick={function () {
                      addChild();
                      setActiveChildIdx(children.length); // switch to new child tab
                      window.scrollTo(0, 0);
                    }}
                    style={{ fontSize: 13 }}
                  >
                    + Add Child
                  </Btn>
                )}
                <Btn onClick={goToStep3} style={{ padding: "11px 32px", fontSize: 14 }}>
                  Review &amp; submit {GLYPH.arrowRight}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 (Stock Top-up): the six-section capture (isolated component). ── */}
        {step === 2 && requestType === "stock_top_up" && (
          <StockTopUpStep
            state={topUpState}
            setState={setTopUpState}
            onBack={goBack}
            onSubmit={handleTopUpSubmit}
            readOnly={!!initialData?.prefillId}
            // Require "On hand" for every ordered item when a DFV Service creates its own
            // top-up; a staff-prepared prefill (read-only review) is exempt.
            requireOnHand={!initialData?.prefillId}
          />
        )}

        {/* ── STEP 3: Review & Submit ── */}
        {step === 3 && (
          <div>
            {/* General summary */}
            <Card style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#00695C",
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>General Information</span>
                <button
                  onClick={function () {
                    setStep(1);
                    window.scrollTo(0, 0);
                  }}
                  style={{
                    background: "none",
                    border: `1px solid ${"#00897B"}`,
                    color: "#00695C",
                    borderRadius: 5,
                    padding: "3px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Edit
                </button>
              </div>
              <SummaryRow label="Client Reference" value={general.clientRef || "—"} />
              <SummaryRow label="Contact Phone" value={formatPhone(general.phone) || "—"} />
              {general.mobile ? (
                <SummaryRow label="Contact Mobile" value={formatPhone(general.mobile)} />
              ) : null}
              <SummaryRow label="Delivery Address" value={general.delivery || "—"} />
              {general.notes?.trim() && (
                <SummaryRow label="Notes for Zephyr" value={general.notes} />
              )}
            </Card>

            {/* Children summary */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#00695C", marginBottom: 10 }}>
                {"Children & Needs (" +
                  children.length +
                  " child" +
                  (children.length !== 1 ? "ren" : "") +
                  ")"}
              </div>
              {children.map(function (child, i) {
                return React.createElement(ChildSummaryCard, {
                  key: child._key || i,
                  child: child,
                  idx: i,
                });
              })}
            </div>

            {/* Files — collapsible */}
            {/* Note: div[role=button] used here instead of <button> to avoid */}
            {/* button-inside-button nesting — HelpButton renders its own <button>. */}
            <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
              <div
                role="button"
                tabIndex={0}
                onClick={function () {
                  setFilesOpen(function (o) {
                    return !o;
                  });
                }}
                onKeyDown={function (e) {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setFilesOpen(function (o) {
                      return !o;
                    });
                  }
                }}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: "14px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div
                  className="files-flash"
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#00695C",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 18, textDecoration: "underline", color: "#004D40" }}>
                    Add Files/Documents
                  </span>
                  {(docs || []).filter(Boolean).length > 0 && (
                    <span
                      style={{
                        background: "#00897B",
                        color: "white",
                        borderRadius: 10,
                        padding: "1px 8px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {(docs || []).filter(Boolean).length}
                    </span>
                  )}
                  <span style={{ fontSize: 18, color: "#607D79", fontWeight: 400 }}>
                    (optional — supporting documents) <HelpButton helpKey="uploads" user={user} />
                  </span>
                </div>
                <span style={{ fontSize: 13, color: "#00897B", fontWeight: 600 }}>
                  {filesOpen ? "^ collapse" : "v expand"}
                </span>
              </div>
              {filesOpen && (
                <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${"#CFE0DE"}` }}>
                  <TabFiles
                    invoices={inv}
                    setInvoices={setInv}
                    docs={docs}
                    setDocs={setDocs}
                    showInvoices={false}
                    req={Object.assign({}, initialData || {}, {
                      refNo: initialData?.refNo || draftRefNo || "Draft",
                      shelterCode: initialData?.shelterCode || user.shelterCode || "",
                      shelterName: initialData?.shelterName || user.shelterName || "",
                    })}
                    user={user}
                  />
                </div>
              )}
            </Card>

            {/* Total */}
            {grand > 0 && (
              <div
                style={{
                  background: "#00695C",
                  color: "#FFFFFF",
                  borderRadius: 10,
                  padding: 20,
                  marginBottom: 20,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, opacity: 0.8 }}>
                  REQUEST TOTAL
                </div>
                {childFeeTotal > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 5,
                      fontSize: 14,
                    }}
                  >
                    <span>School Costs &amp; Stationery</span>
                    <span>{money(childFeeTotal)}</span>
                  </div>
                )}
                {childUniTotal > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 5,
                      fontSize: 14,
                    }}
                  >
                    <span>Uniforms</span>
                    <span>{money(childUniTotal)}</span>
                  </div>
                )}
                {childShoeTotal > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 5,
                      fontSize: 14,
                    }}
                  >
                    <span>Shoes</span>
                    <span>{money(childShoeTotal)}</span>
                  </div>
                )}
                {childLapTotal > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 5,
                      fontSize: 14,
                    }}
                  >
                    <span>Device</span>
                    <span style={{ fontSize: 12 }}>
                      {money(childLapTotal)}{" "}
                      <span style={{ fontSize: 11, opacity: 0.7 }}>est.</span>
                    </span>
                  </div>
                )}
                <div
                  style={{
                    borderTop: "1px solid rgba(255,255,255,0.3)",
                    marginTop: 8,
                    paddingTop: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  <span>TOTAL</span>
                  <span>{money(grand)}</span>
                </div>
              </div>
            )}

            {/* What happens next */}
            <div
              style={{
                background: "#E8F5E9",
                border: "1px solid #A5D6A7",
                borderRadius: 10,
                padding: "14px 18px",
                marginBottom: 20,
                fontSize: 13,
                color: "#1B5E20",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>What happens next?</div>
              <div>
                Once submitted, a member of the Zephyr team will review your request, typically
                within 24 hours. You will be notified when there is an update. This is not an
                approval.
              </div>
            </div>

            {/* Submit error */}
            {submitErr && (
              <div
                style={{
                  background: "#FFEBEE",
                  border: "1px solid #EF9A9A",
                  borderRadius: 10,
                  padding: "12px 16px",
                  marginBottom: 16,
                  fontSize: 13,
                  color: "#C62828",
                }}
              >
                <strong>Please fix the following before submitting:</strong>
                <ul style={{ margin: "6px 0 0", paddingLeft: 20, lineHeight: 1.8 }}>
                  {submitErr.split(" · ").map(function (e, i) {
                    return e.trim() ? React.createElement("li", { key: i }, e.trim()) : null;
                  })}
                </ul>
              </div>
            )}

            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "white",
                borderTop: `1px solid ${"#CFE0DE"}`,
                padding: "12px 20px",
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                zIndex: 10,
                borderRadius: "0 0 10px 10px",
              }}
            >
              <Btn variant="secondary" onClick={goBack}>
                {GLYPH.arrowLeft} Back
              </Btn>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Btn variant="secondary" onClick={saveDraftAndExit} disabled={submitting}>
                  Save Draft &amp; Exit
                </Btn>
                <Btn
                  onClick={function () {
                    setShowSubmitConfirm(true);
                  }}
                  disabled={submitting}
                  style={{
                    padding: "11px 32px",
                    fontSize: 14,
                    background: "#2E7D32",
                    color: "white",
                  }}
                >
                  {submitting ? "Submitting..." : `Submit Request ${GLYPH.arrowRight}`}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </div>
      {showSubmitConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            fontFamily: "inherit",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 14,
              maxWidth: 500,
              width: "100%",
              boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
              padding: 28,
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 12 }}>{GLYPH.warningEmoji}</div>
            <h2 style={{ margin: "0 0 10px", fontSize: 18, color: "#1A2E2B" }}>Ready to submit?</h2>
            <p style={{ fontSize: 14, color: "#607D79", marginBottom: 8, lineHeight: 1.6 }}>
              Please check that you have included <strong>everything needed</strong> for this
              request before submitting.
            </p>
            <p
              style={{
                fontSize: 14,
                color: "#B71C1C",
                fontWeight: 600,
                marginBottom: 20,
                lineHeight: 1.6,
              }}
            >
              Once submitted, you will not be able to edit this request. If anything is missing, use{" "}
              <strong>Save Draft</strong> instead and come back when ready.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn
                variant="ghost"
                onClick={function () {
                  setShowSubmitConfirm(false);
                }}
              >
                Go Back &amp; Check
              </Btn>
              <Btn
                variant="gold"
                onClick={function () {
                  setShowSubmitConfirm(false);
                  handleSubmit();
                }}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Yes, Submit Now →"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// RequestForm moved to ./RequestForm.jsx — imported above.

// --- DASHBOARD ------------------------------------------------

// ── IMPORT PAGE ─────────────────────────────────────────────────────────────

export { ChildTab, ChildTabBar, RequestForm, RequestOverview, RequestWizard, TabFiles, TabGeneral };
